import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit3, ExternalLink, Plus, Search } from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DataGrid } from "@/components/ui/data-grid";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { createContractor, listContractors, updateContractor } from "@/services/dataService";

function normalizeWebsite(value) {
  const trimmed = (value || "").trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  return `https://${trimmed}`;
}

function parseSpecialty(value) {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

const EMPTY_FORM = {
  companyName: "",
  contactName: "",
  phone: "",
  email: "",
  address: "",
  website: "",
  notes: "",
  specialty: "",
};

export default function Contractors() {
  const { currentUser } = useAuth();
  const userId = currentUser?._id;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState("all");
  const [editing, setEditing] = useState(null); // null | "new" | contractor
  const [form, setForm] = useState(EMPTY_FORM);

  const contractorsQuery = useQuery({
    queryKey: ["contractors", userId, "all"],
    queryFn: () => listContractors(),
    enabled: Boolean(userId),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["contractors", userId] });

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      editing === "new" ? createContractor(payload) : updateContractor({ id: editing._id, ...payload }),
    onSuccess: async () => {
      await invalidate();
      setEditing(null);
      setForm(EMPTY_FORM);
      toast({ title: editing === "new" ? "Contractor added" : "Contractor updated" });
    },
    onError: (error) =>
      toast({ title: "Could not save", description: error.message, variant: "destructive" }),
  });

  const quickCreateMutation = useMutation({
    mutationFn: createContractor,
    onSuccess: async () => {
      await invalidate();
      toast({ title: "Contractor added" });
    },
    onError: (error) =>
      toast({ title: "Could not add", description: error.message, variant: "destructive" }),
  });

  function handleQuickAdd(draft, reset) {
    const companyName = (draft.companyName || "").trim();
    const phone = (draft.phone || "").trim();
    if (!companyName || !phone) {
      toast({ title: "Company and phone are required", variant: "destructive" });
      return;
    }
    quickCreateMutation.mutate(
      {
        companyName,
        contactName: (draft.contactName || "").trim() || companyName,
        phone,
        email: "",
        specialty: parseSpecialty(draft.specialty || ""),
        isPreferred: false,
      },
      { onSuccess: () => reset() }
    );
  }

  const specialties = useMemo(() => {
    const set = new Set();
    for (const contractor of contractorsQuery.data || []) {
      for (const item of contractor.specialty || []) set.add(item);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [contractorsQuery.data]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (contractorsQuery.data || []).filter((contractor) => {
      if (specialtyFilter !== "all" && !(contractor.specialty || []).includes(specialtyFilter)) {
        return false;
      }
      if (!term) return true;
      return [contractor.companyName, contractor.contactName, contractor.phone, contractor.email, contractor.address, contractor.website]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [contractorsQuery.data, searchTerm, specialtyFilter]);

  function openNew() {
    setForm(EMPTY_FORM);
    setEditing("new");
  }

  function openEdit(contractor) {
    setForm({
      companyName: contractor.companyName || "",
      contactName: contractor.contactName || "",
      phone: contractor.phone || "",
      email: contractor.email || "",
      address: contractor.address || "",
      website: contractor.website || "",
      notes: contractor.notes || "",
      specialty: (contractor.specialty || []).join(", "),
    });
    setEditing(contractor);
  }

  function submit() {
    saveMutation.mutate({
      companyName: form.companyName.trim(),
      contactName: form.contactName.trim() || form.companyName.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      address: form.address.trim() || undefined,
      website: form.website.trim() || undefined,
      notes: form.notes.trim() || undefined,
      specialty: parseSpecialty(form.specialty),
      isPreferred: false,
    });
  }

  const field = (key) => (event) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const columns = [
    {
      key: "companyName",
      header: "Company",
      sortable: true,
      render: (c) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-foreground">{c.companyName}</p>
          {c.contactName && c.contactName !== c.companyName ? (
            <p className="truncate text-xs text-muted-foreground">{c.contactName}</p>
          ) : null}
        </div>
      ),
    },
    {
      key: "specialty",
      header: "Category",
      render: (c) =>
        (c.specialty || []).length ? (
          <div className="flex flex-wrap gap-1">
            {c.specialty.map((item) => (
              <Badge key={item} variant="outline" className="text-[11px]">
                {item}
              </Badge>
            ))}
          </div>
        ) : (
          "—"
        ),
    },
    {
      key: "phone",
      header: "Phone",
      sortable: true,
      render: (c) =>
        c.phone ? (
          <a
            href={`tel:${c.phone}`}
            onClick={(event) => event.stopPropagation()}
            className="text-primary underline-offset-4 hover:underline"
          >
            {c.phone}
          </a>
        ) : (
          "—"
        ),
    },
    {
      key: "email",
      header: "Email",
      render: (c) =>
        c.email ? (
          <a
            href={`mailto:${c.email}`}
            onClick={(event) => event.stopPropagation()}
            className="text-primary underline-offset-4 hover:underline"
          >
            {c.email}
          </a>
        ) : (
          "—"
        ),
    },
    {
      key: "website",
      header: "Site",
      render: (c) =>
        c.website ? (
          <a
            href={normalizeWebsite(c.website)}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open
          </a>
        ) : (
          "—"
        ),
    },
  ];
  columns[0].footer = `${filtered.length} contractor${filtered.length === 1 ? "" : "s"}`;

  const quickAdd = {
    addLabel: "Add contractor",
    submitting: quickCreateMutation.isPending,
    onSubmit: handleQuickAdd,
    fields: [
      { key: "companyName", placeholder: "Company", autoFocus: true },
      { key: "specialty", placeholder: "Category" },
      { key: "phone", placeholder: "Phone" },
    ],
  };

  return (
    <PageLayout
      title="Contractors"
      eyebrow="Directory"
      subtitle="Your trusted vendors, sortable and one tap to call."
      onRefresh={() => contractorsQuery.refetch()}
      isRefreshing={contractorsQuery.isFetching}
    >
      <div className="space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search company, contact, phone…"
                className="pl-9"
              />
            </div>
            <select
              value={specialtyFilter}
              onChange={(event) => setSpecialtyFilter(event.target.value)}
              className="h-10 rounded-md border bg-background px-3 text-sm"
            >
              <option value="all">All categories</option>
              {specialties.map((specialty) => (
                <option key={specialty} value={specialty}>
                  {specialty}
                </option>
              ))}
            </select>
          </div>
          <Button onClick={openNew} className="gap-2">
            <Plus className="h-4 w-4" />
            Add with details
          </Button>
        </div>

        <DataGrid
          columns={columns}
          rows={filtered}
          getRowId={(c) => c._id}
          onRowClick={openEdit}
          initialSort={{ key: "companyName", dir: "asc" }}
          quickAdd={quickAdd}
          emptyState={
            searchTerm || specialtyFilter !== "all"
              ? "No matching contractors."
              : "Type a company and phone below, then press Enter."
          }
          rowActions={(c) => (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                openEdit(c);
              }}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Edit contractor"
            >
              <Edit3 className="h-4 w-4" />
            </button>
          )}
        />

        <p className="px-1 text-xs text-muted-foreground">
          Type a company, category, and phone in the bottom row to add fast. Click any row for full
          details, email, address, and notes.
        </p>
      </div>

      <Dialog open={Boolean(editing)} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Add contractor" : "Edit contractor"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Company</Label>
                <Input value={form.companyName} onChange={field("companyName")} />
              </div>
              <div className="space-y-1">
                <Label>Contact</Label>
                <Input value={form.contactName} onChange={field("contactName")} />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Phone</Label>
                <Input value={form.phone} onChange={field("phone")} />
              </div>
              <div className="space-y-1">
                <Label>Email</Label>
                <Input value={form.email} onChange={field("email")} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Address</Label>
              <Input value={form.address} onChange={field("address")} />
            </div>
            <div className="space-y-1">
              <Label>Website</Label>
              <Input value={form.website} onChange={field("website")} />
            </div>
            <div className="space-y-1">
              <Label>Categories (comma separated)</Label>
              <Input value={form.specialty} onChange={field("specialty")} />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={field("notes")} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={saveMutation.isPending || !form.companyName.trim() || !form.phone.trim()}
            >
              {saveMutation.isPending ? "Saving…" : editing === "new" ? "Save contractor" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
